import type { Position } from '../../types/problems';

/**
 * A seat named in full on wide screens ("North") and abbreviated on phones ("N").
 *
 * Both forms are in the DOM and swapped by breakpoint, because a JS width check would
 * flash the wrong one on first paint and would not follow a resize. Only ONE of them is
 * ever exposed to assistive tech: the abbreviation is `aria-hidden` and the full name
 * carries an `sr-only` twin, so a screen reader always hears "North" and never "N" —
 * and never both.
 */

export const SEAT_NAMES: Record<Position, string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

/** True for the four real seats. Guards against the "?" the decoder emits for bad data. */
export function isSeat(value: string | null | undefined): value is Position {
  return value === 'N' || value === 'E' || value === 'S' || value === 'W';
}

export interface SeatLabelProps {
  seat: string | null | undefined;
  /** Rendered when the seat is unknown — defaults to nothing at all. */
  fallback?: string;
  /**
   * Abbreviate on a portrait tablet (`tablet-pt`) as well as on a phone. Off by default, so
   * "Jeff holds — West" on Predicted Jeff's Bids and the conceptual hands keep the full name
   * (spec 031 FR-011). The table session's hand panel opts in (client report, 2026-09-24): on a
   * 768-wide iPad its heading was the widest thing in the hand column, which is sized to its
   * content, and the auction beside it got what was left — too little for four "Pass" cells.
   */
  letterOnTabletPortrait?: boolean;
}

export function SeatLabel({ seat, fallback = '', letterOnTabletPortrait = false }: SeatLabelProps) {
  if (!isSeat(seat)) {
    return fallback ? <span data-testid="seat-label">{fallback}</span> : null;
  }

  // The sr-only twin needs the variant too: `sm:hidden` is `display: none`, which sr-only does
  // not undo, so without it a screen reader would hear nothing on a tablet.
  const tablet = letterOnTabletPortrait ? ' tablet-pt:inline' : '';
  return (
    <span data-testid="seat-label">
      <span className={`sm:hidden${tablet}`} aria-hidden="true">
        {seat}
      </span>
      <span className={`sr-only sm:hidden${tablet}`}>{SEAT_NAMES[seat]}</span>
      <span className={`hidden sm:inline${letterOnTabletPortrait ? ' tablet-pt:hidden' : ''}`}>
        {SEAT_NAMES[seat]}
      </span>
    </span>
  );
}
